import React, { useState, useMemo, useRef } from "react";

/* ────────────────────────────────────────────────────────────────
   Interview Builder — v12 · "warm editorial studio" re-skin
   Applied from the innovative-ux-designer skill:
   · Re-themed to the app's immersive dark system (tokens mapped onto the
     original .rt-scope variables): dark glass cards, violet creator accent
     (--acc #c084fc) with dark ink on accent fills, orange pop. Renders
     IN-FLOW inside the studio/community content area — the sidebar stays
     visible. System font stack (was Fraunces serif) for
     headlines, wordmark, part names, editorial numerals;
     Spline Sans for UI/body; Spline Sans Mono for data
     (counts, pts, minutes) with tabular numerals.
   · Atmosphere: soft radial gradient washes + fine SVG grain
     overlay; masthead header with a strong ink rule; layered
     warm shadows; hover lift on buttons and format cards.
   · Accessibility kept: WCAG-AA contrast on all text tokens,
     2px visible focus rings, larger touch targets on coarse
     pointers, reduced-motion respected.
   Functionality is identical to v11 (formats, marks, ghost rows).
   ──────────────────────────────────────────────────────────────── */

const css = `
.rt-scope{
  --bg:transparent; --card:rgba(255,255,255,0.045); --line:rgba(255,255,255,0.10); --line2:rgba(255,255,255,0.16);
  --ink:#eaecf7; --ink2:#a3aac6; --ink3:#6b7189;
  --acc:#c084fc; --acc-deep:#8b5cf6; --acc-soft:rgba(192,132,252,0.14); --acc-line:rgba(192,132,252,0.40);
  --pop:#fb923c; --pop-soft:rgba(251,146,60,0.12); --pop-line:rgba(251,146,60,0.35);
  --need:#fcd34d; --need-bg:rgba(251,191,36,0.12); --need-line:rgba(251,191,36,0.35);
  --miss:#fca5a5; --miss-bg:rgba(248,113,113,0.12); --miss-line:rgba(248,113,113,0.35);
  --ok:#6ee7b7; --ok-bg:rgba(52,211,153,0.12); --ok-line:rgba(52,211,153,0.35);
  --font-d:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,Roboto,sans-serif;
  --font-b:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,Roboto,sans-serif;
  --font-m:ui-monospace,'SF Mono',Menlo,Consolas,monospace;
  --shadow-s:0 1px 2px rgba(0,0,0,.40);
  --shadow-m:0 8px 24px rgba(0,0,0,.35);
}
.ib{position:relative;color:var(--ink);font-family:var(--font-b);min-height:100%;font-size:14px;
  background:
    radial-gradient(1100px 480px at 88% -8%, rgba(192,132,252,.10), transparent 60%),
    radial-gradient(900px 420px at -6% -4%, rgba(251,146,60,.05), transparent 55%),
    var(--bg);}
.ib *{box-sizing:border-box;}
.wordmark{font-family:var(--font-d);font-weight:600;font-size:15px;letter-spacing:.18em;color:var(--ink);display:inline-flex;align-items:center;gap:9px;}
.wordmark::before{content:"";width:9px;height:9px;background:var(--pop);transform:rotate(45deg);border-radius:2px;flex:none;}
.h1{font-family:var(--font-d);font-weight:600;font-size:26px;letter-spacing:-.015em;line-height:1.15;}
.h2{font-family:var(--font-d);font-weight:600;font-size:17px;letter-spacing:-.01em;}
.secno{display:inline-flex;align-items:center;gap:8px;font-family:var(--font-b);font-weight:700;font-size:10.5px;letter-spacing:.16em;text-transform:uppercase;color:var(--ink2);}
.secno::before{content:"";width:16px;height:2px;background:var(--pop);flex:none;}
.card{background:var(--card);border:1px solid var(--line);border-radius:14px;box-shadow:var(--shadow-s);}
.fld{width:100%;background:var(--card);border:1px solid var(--line2);border-radius:9px;padding:9px 11px;font:inherit;color:var(--ink);transition:border-color .15s ease;}
.fld::placeholder{color:var(--ink3);}
.fld:hover{border-color:var(--ink3);}
input[type=number].fld{font-family:var(--font-m);font-size:13px;font-variant-numeric:tabular-nums;}
.fld:focus,.btn:focus,.chip:focus,.namefld:focus,.fmt:focus,select:focus,input[type=range]:focus,input[type=checkbox]:focus{outline:2px solid var(--acc);outline-offset:2px;}
textarea.fld{resize:vertical;line-height:1.55;}
select.fld{cursor:pointer;}
.btn{border:1px solid var(--line2);background:var(--card);color:var(--ink);border-radius:10px;padding:8px 14px;font:inherit;font-weight:600;font-size:13px;cursor:pointer;box-shadow:0 1px 0 rgba(0,0,0,.30);transition:transform .16s ease, box-shadow .16s ease, border-color .16s ease, color .16s ease;}
.btn:hover{border-color:var(--acc);color:var(--acc);transform:translateY(-1px);box-shadow:0 4px 12px -6px rgba(0,0,0,.40);}
.btn:active{transform:translateY(0);box-shadow:0 1px 0 rgba(0,0,0,.30);}
.btn-acc{background:var(--acc);border-color:transparent;color:#0b0d14;box-shadow:0 8px 24px rgba(192,132,252,.30);}
.btn-acc:hover{color:#0b0d14;filter:brightness(1.06);transform:translateY(-1px);}
.btn-acc:active{transform:translateY(1px);box-shadow:inset 0 1px 0 rgba(255,255,255,.16),0 1px 0 var(--acc-deep);}
.btn-acc:disabled{background:var(--line2);border-color:var(--line2);color:#8D8272;cursor:not-allowed;filter:none;transform:none;box-shadow:none;}
.btn-ghost{border:0;background:transparent;color:var(--acc);padding:6px 8px;font-weight:600;cursor:pointer;font:inherit;font-size:13px;}
.btn-ghost:hover{color:var(--acc-deep);text-decoration:underline;text-underline-offset:3px;}
.btn-ico{border:1px solid transparent;background:transparent;color:var(--ink3);border-radius:8px;padding:4px 7px;cursor:pointer;font:inherit;font-size:12.5px;transition:background .12s ease,color .12s ease;}
.btn-ico:hover{background:var(--acc-soft);color:var(--acc);}
.chip{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--line2);border-radius:999px;padding:4px 12px;font-size:12.5px;color:var(--ink2);background:var(--card);cursor:pointer;user-select:none;font-weight:500;transition:border-color .14s ease,background .14s ease,color .14s ease;}
.chip:hover{border-color:var(--acc-line);}
.chip.on{background:var(--acc-soft);border-color:var(--acc-line);color:var(--acc);font-weight:600;}
.fld.mini{width:54px;padding:2px 6px;font-size:12px;text-align:center;}
.qmeta{display:flex;align-items:center;gap:5px;flex-wrap:wrap;}
.chip.static{cursor:default;}
.chip.static:hover{border-color:var(--line2);}
.seg{display:inline-flex;border:1px solid var(--line2);border-radius:10px;overflow:hidden;background:var(--card);flex-wrap:wrap;}
.seg button{border:0;background:transparent;padding:7px 12px;font:inherit;font-size:12.5px;color:var(--ink2);cursor:pointer;border-right:1px solid var(--line);}
.seg button:last-child{border-right:0;}
.seg .on{background:var(--acc);color:#0b0d14;font-weight:600;}
.status{font-size:11px;font-weight:600;border-radius:999px;padding:2px 9px;white-space:nowrap;font-family:var(--font-m);letter-spacing:.01em;}
.st-ok{background:var(--ok-bg);color:var(--ok);border:1px solid var(--ok-line);}
.st-need{background:var(--need-bg);color:var(--need);border:1px solid var(--need-line);}
.label{font-size:11px;font-weight:600;color:var(--ink2);letter-spacing:.06em;text-transform:uppercase;margin-bottom:6px;display:block;}
.sub{color:var(--ink2);font-size:12.5px;line-height:1.55;}
.row{display:flex;gap:8px;align-items:center;}
.frow{display:flex;gap:14px;flex-wrap:wrap;}
.frow>div{flex:1;min-width:180px;}
.check{display:flex;gap:9px;align-items:center;font-size:13px;}
.ckdot{width:16px;height:16px;border-radius:99px;border:2px solid var(--line2);flex:none;display:flex;align-items:center;justify-content:center;font-size:10px;color:#0b0d14;}
.ckdot.done{background:var(--ok);border-color:var(--ok);}
.tip{display:flex;gap:10px;align-items:flex-start;border-radius:10px;padding:9px 12px;font-size:12.5px;line-height:1.5;background:var(--need-bg);border:1px solid var(--need-line);color:#6B4A0E;}
.miss{background:var(--miss-bg);border:1px solid var(--miss-line);color:#7C2C24;}
.bubble{border-radius:12px;padding:10px 13px;font-size:13px;line-height:1.55;max-width:85%;}
.bub-a{background:var(--acc-soft);border:1px solid var(--acc-line);align-self:flex-start;}
.bub-l{background:var(--bg);border:1px solid var(--line2);align-self:flex-end;color:var(--ink2);font-style:italic;}
.rt-scroll{overflow-x:auto;border:1px solid var(--line2);border-radius:10px;background:var(--card);}
.rt-grid{display:grid;grid-template-columns:minmax(180px,1.15fr) minmax(200px,1.5fr) 118px 36px;min-width:560px;}
.rt-h{font-size:10px;font-weight:600;letter-spacing:.09em;text-transform:uppercase;color:var(--ink2);padding:8px 11px;background:rgba(255,255,255,0.03);border-bottom:1px solid var(--line2);}
.rt-c{padding:3px 4px;display:flex;align-items:center;}
.rt-in{border:1px solid transparent;background:transparent;width:100%;padding:7px 8px;font:inherit;font-size:13px;color:var(--ink);border-radius:7px;}
.rt-in::placeholder{color:var(--ink3);}
.rt-in:focus{outline:2px solid var(--acc);background:var(--card);}
textarea.rt-in{resize:none;overflow:hidden;line-height:1.5;min-height:36px;font-size:13.5px;}
select.rt-in{cursor:pointer;}
.ghost-in::placeholder{font-style:italic;}
/* format picker */
.fmtgrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:12px;}
.fmt{position:relative;text-align:left;border:1.5px solid var(--line2);border-radius:14px;background:var(--card);padding:16px;display:grid;gap:8px;cursor:pointer;font:inherit;align-content:start;box-shadow:var(--shadow-s);transition:transform .16s ease, box-shadow .16s ease, border-color .16s ease;}
.fmt:hover{transform:translateY(-2px);border-color:var(--acc-line);box-shadow:var(--shadow-m);}
.fmt.on{border-color:var(--acc);box-shadow:0 0 0 3px var(--acc-soft),var(--shadow-m);background:var(--card);}
.fmt.soon{opacity:.55;cursor:not-allowed;}
.fmt.soon:hover{transform:none;border-color:var(--line2);box-shadow:var(--shadow-s);}
.fmtno{font-family:var(--font-d);font-style:italic;font-weight:600;font-size:27px;line-height:1;color:var(--line2);}
.fmt.on .fmtno{color:var(--pop);}
.fmtname{font-family:var(--font-d);font-weight:600;font-size:16px;letter-spacing:-.01em;}
/* workspace: two panes */
.ws2{display:grid;grid-template-columns:252px 1fr;height:calc(100vh - 58px);min-height:0;}
.pane{min-height:0;overflow-y:auto;}
.rail{background:var(--card);border-right:1px solid var(--line);display:flex;flex-direction:column;min-height:0;}
.rail-h{padding:14px 14px 8px;border-bottom:1px solid var(--line);flex:none;}
.rail-h .sub{font-family:var(--font-m);font-size:11px;letter-spacing:0;}
.rail-b{flex:1;overflow-y:auto;padding:8px;min-height:0;}
.rail-f{flex:none;padding:10px;border-top:1px solid var(--line);}
.qrow{display:flex;gap:8px;align-items:center;width:100%;text-align:left;border:1px solid transparent;background:transparent;padding:9px 10px;border-radius:10px;font:inherit;cursor:pointer;color:var(--ink);transition:background .12s ease,border-color .12s ease;}
.qrow:hover{background:var(--acc-soft);}
.qrow.on{background:var(--acc-soft);border-color:var(--acc-line);}
.qrow .sub{font-family:var(--font-m);font-size:10.5px;letter-spacing:0;}
.qrow .ops{display:none;flex:none;gap:0;}
.qrow:hover .ops{display:flex;}
.dot{width:8px;height:8px;border-radius:99px;flex:none;}
.stg-num{width:23px;height:23px;border-radius:99px;background:var(--pop-soft);border:1px solid var(--pop-line);color:var(--pop);font-family:var(--font-d);font-style:italic;font-weight:600;font-size:12px;display:flex;align-items:center;justify-content:center;flex:none;}
.detail{background:var(--bg);padding:16px 18px 60px;overflow-y:auto;min-height:0;}
.dwrap{max-width:980px;display:grid;gap:12px;}
.pnav{position:sticky;top:-16px;z-index:5;background:var(--bg);padding:12px 0 8px;display:flex;gap:6px;flex-wrap:wrap;}
.pnav .chip{font-size:12px;padding:3px 10px;}
.psec{border-top:1px solid var(--line);padding-top:14px;display:grid;gap:10px;scroll-margin-top:64px;}
.psec:first-of-type{border-top:0;padding-top:0;}
.namefld{border:1px solid transparent;background:transparent;font:inherit;font-weight:600;color:var(--ink);border-radius:7px;padding:4px 8px;min-width:120px;}
.namefld:hover{border-color:var(--line2);}
.pname{border:1px solid transparent;background:transparent;font-family:var(--font-d);font-weight:600;font-size:16px;color:var(--ink);border-radius:7px;padding:4px 8px;flex:1;min-width:160px;letter-spacing:-.01em;}
.pname::placeholder{font-family:var(--font-b);font-size:14px;font-weight:500;}
.pname:hover{border-color:var(--line2);}
.pname:focus{outline:2px solid var(--acc);background:var(--card);}
.mono{font-family:var(--font-m);font-variant-numeric:tabular-nums;letter-spacing:0;}
.snack{position:fixed;bottom:18px;left:50%;transform:translateX(-50%);background:#12141d;border:1px solid rgba(255,255,255,0.16);color:#eaecf7;border-radius:12px;padding:11px 16px;display:flex;gap:14px;align-items:center;font-size:13px;z-index:60;box-shadow:0 14px 38px rgba(0,0,0,.55);}
.overlay{position:fixed;inset:0;background:rgba(35,29,21,.5);z-index:50;display:flex;align-items:center;justify-content:center;padding:16px;}
.modal{background:var(--card);border-radius:16px;max-width:580px;width:100%;max-height:85vh;overflow:auto;padding:20px;display:grid;gap:14px;box-shadow:var(--shadow-m);}
.setupgrid{display:grid;grid-template-columns:1fr 1fr;gap:14px;align-items:start;}
.revgrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(330px,1fr));gap:12px;}
.span-all{grid-column:1/-1;}
.fadein{animation:fi .22s ease;}
@keyframes fi{from{opacity:0;transform:translateY(4px);}to{opacity:1;transform:none;}}
@media (prefers-reduced-motion:reduce){.fadein{animation:none;}.btn,.fmt,.chip,.fld,.btn-ico,.qrow{transition:none;}}
@media (hover:none){.qrow .ops{display:flex;}.btn-ico{padding:9px 11px;}}
@media (max-width:1019px){
  .ws2{display:block;height:auto;}
  .rail{border-right:0;border-bottom:1px solid var(--line);}
  .rail-b{display:flex;flex-direction:row;overflow-x:auto;gap:8px;padding:10px;}
  .qrow{flex:0 0 auto;width:240px;border-color:var(--line2);}
  .qrow .ops{display:flex;}
  .detail{padding:14px;}
  .setupgrid{grid-template-columns:1fr;}
}
`;

/* ── data ──────────────────────────────────────────────────── */
let _id = 900;
const nid = () => "n" + _id++;
const TYPES = { warmup: "Warm-up", behavioral: "Behavioral", system: "System design", estimation: "Estimation", custom: "Custom" };
const PRESSURE = { 1: "Friendly", 2: "Warm", 3: "Balanced", 4: "Probing", 5: "Bar-raiser" };

/* The format catalog — each entry is an authoring schema + an
   agent behavior profile + a report type. */
const FORMATS = [
  {
    id: "structured", name: "Structured deep-dive", tag: "Scored / 100",
    desc: "Questions broken into conversation parts, each graded against your rubric — pitfalls, nudges, curveballs, and marks.",
    agent: "Digs until every must-cover is met; probes, drops curveballs, nudges in practice mode.",
    report: "Rubric evidence per part, scored out of 100.",
  },
  {
    id: "simple", name: "Simple script", tag: "Conversational",
    desc: "An introduction, a plain list of questions, and an ending. Nothing else to set up.",
    agent: "Asks your questions in order, exactly as written, and keeps the session moving.",
    report: "Full transcript plus a short AI summary — no scores.",
  },
  {
    id: "soon", soon: true, name: "More formats", tag: "Coming soon",
    desc: "Case-study walkthroughs, rapid-fire screens, take-home debriefs — each a new schema that tunes the agent differently.",
    agent: "—", report: "—",
  },
];

const newPart = (name, must = false) => ({ id: nid(), name, must, rubric: [], nudge: "", pitfalls: [], curveball: "" });
const newPoint = (text = "", importance = "good", remark = "") => ({ id: nid(), text, importance, remark });

const baseQ = () => ({
  id: nid(), type: "custom", minutes: 5, marks: 10, prompt: "",
  flow: [newPart("The answer", true)],
});

const typePrefill = (t) => {
  const q = { ...baseQ(), type: t };
  if (t === "behavioral") Object.assign(q, { minutes: 8, marks: 20, flow: [
    newPart("The situation & their stake"), newPart("What they actually did", true), newPart("Result, then reflection")] });
  if (t === "system") Object.assign(q, { minutes: 20, marks: 30, flow: [
    newPart("Clarify what we're building", true), newPart("Estimate the scale"), newPart("High-level design"),
    newPart("Deep dive on one part", true), newPart("Tradeoffs and cost")] });
  if (t === "estimation") Object.assign(q, { minutes: 6, marks: 15, flow: [
    newPart("State assumptions", true), newPart("Compute"), newPart("Sanity check")] });
  if (t === "warmup") Object.assign(q, { minutes: 3, marks: 5, flow: [newPart("The story", true)] });
  return q;
};

const SEED_MODEL_ANSWER = "Across this interview, strong candidates share one shape: they scope before they solve, say assumptions and numbers out loud, own their part of every story, and close each answer with an outcome plus a lesson. On the system design, listen for volume-first scoping and priority lanes; on the estimation, stated assumptions and a sanity-checked final number.";

const DRAFT = () => {
  const q0 = { ...typePrefill("warmup"), marks: 10,
    prompt: "Walk me through a project from the last year that you're proud of." };
  q0.flow = [{ ...newPart("The story", true),
    rubric: [
      { ...newPoint("Tells it as a story: context, what they did, the outcome", "must", "One tight minute, beginning-middle-end.") },
      { ...newPoint("Separates their own role from the team's", "good", "'I' for their part, 'we' for the team's.") }],
    nudge: "Give it a shape: where, what you did, what it led to." }];

  const q1 = { ...typePrefill("behavioral"), marks: 25,
    prompt: "Tell me about a time a client asked for something you believed was the wrong solution." };
  q1.flow = [
    { ...newPart("The situation & their stake"),
      rubric: [{ ...newPoint("Sets the scene in under a minute", "good", "Who the client was, what they asked for, why it mattered.") }] },
    { ...newPart("The disagreement", true),
      rubric: [
        { ...newPoint("Gives a concrete reason the ask was wrong", "must", "A metric, a constraint, a past incident — not a feeling.") },
        { ...newPoint("Disagrees without damaging the relationship", "good", "Private-first, framed around the client's goal.") }],
      nudge: "What did the numbers say at that point?",
      pitfalls: [{ id: nid(), text: "Blames the client instead of owning it", say: "What was reasonable about their ask, from where they sat?" }] },
    { ...newPart("Result, then reflection"),
      rubric: [
        { ...newPoint("Explains what actually shipped in the end", "good", "A real compromise or pilot, with what happened next.") },
        { ...newPoint("Reflects on what they'd do differently", "bonus", "An unprompted lesson.") }],
      curveball: "If they had escalated to your manager instead — what would you have done?" }];

  const q2 = { ...typePrefill("system"), minutes: 22, marks: 40,
    prompt: "Design the notification system for a used-car marketplace — price-drop alerts, auction bid updates, and service reminders across push, SMS, and WhatsApp." };
  q2.flow = [
    { ...newPart("Clarify what we're building", true),
      rubric: [{ ...newPoint("Asks about volume, channels, urgency before designing", "must", "User count, notifications/day, channel split — before any boxes.") }],
      pitfalls: [{ id: nid(), text: "Starts drawing boxes without asking anything", say: "Hold on — what would you want to know before designing?" }] },
    { ...newPart("Estimate the scale"),
      rubric: [{ ...newPoint("Estimates fan-out and quantifies peak load", "good", "A per-second number for the auction spike.") }] },
    { ...newPart("High-level design"),
      rubric: [{ ...newPoint("Gives different delivery guarantees to different notification types", "good", "Bid updates vs marketing blasts, with a reason.") }],
      curveball: "Marketing wants to blast 5M users in 10 minutes for a flash sale. What breaks first?" },
    { ...newPart("Deep dive: the auction spike", true),
      rubric: [{ ...newPoint("Handles duplicates when things retry", "good", "Dedupe keys or idempotent consumers.") }],
      nudge: "Think about what happens to the queue during an auction spike.",
      pitfalls: [{ id: nid(), text: "Jumps to Kafka / sharding before any numbers", say: "Before we pick infrastructure — what do we know about volume?" }] },
    { ...newPart("Tradeoffs and cost"),
      rubric: [{ ...newPoint("Brings up cost without being asked", "bonus", "Batching, cheaper channels, quiet hours — any one.") }] }];

  const q3 = { ...typePrefill("estimation"), marks: 25,
    prompt: "Estimate how much storage our listing photos will need after two years of growth." };
  q3.flow = [
    { ...newPart("State assumptions", true),
      rubric: [{ ...newPoint("Says their assumptions out loud before computing", "must", "Cars per month, photos per car, size per photo — before math.") }],
      nudge: "Start from how many cars go live each month.",
      pitfalls: [{ id: nid(), text: "Anchors on a random number with no basis", say: "Where does that number come from?" }] },
    { ...newPart("Compute"),
      rubric: [{ ...newPoint("Keeps the arithmetic easy to follow", "good", "Talks through each step, rounds deliberately.") }] },
    { ...newPart("Sanity check"),
      rubric: [{ ...newPoint("Lands a number with units, then sanity-checks it", "good", "Terabytes vs petabytes, checked against something known.") }] }];
  return [q0, q1, q2, q3];
};

/* Seed for the Simple script format */
const SDRAFT = () => [
  { id: nid(), text: "To start — tell me a bit about yourself and what you're working on these days.", minutes: 2 },
  { id: nid(), text: "What drew you to this role?", minutes: 2 },
  { id: nid(), text: "Walk me through a recent project you enjoyed. What was your part in it?", minutes: 3 },
  { id: nid(), text: "What kind of team or environment brings out your best work?", minutes: 2 },
  { id: nid(), text: "Before we wrap — any questions you'd like to ask me?", minutes: 2 },
];

const STOP = new Set(["should", "before", "answers", "strong", "provider", "system", "actually", "client", "question", "answer", "listing", "notification"]);
const titleOf = (q) => ((q.heading || "").trim() ? q.heading.trim() : q.prompt.trim() ? (q.prompt.trim().length > 44 ? q.prompt.trim().slice(0, 44) + "…" : q.prompt.trim()) : "Untitled question");
const allPoints = (q) => q.flow.flatMap((s) => s.rubric);

function neededOf(q) {
  const pts = allPoints(q);
  return [
    { key: "prompt", label: "The question to ask", done: !!q.prompt.trim() },
    { key: "flow", label: "A conversation flow — at least one part", done: q.flow.length > 0 },
    { key: "rubric", label: "Rubric — at least one point in the flow", done: pts.length > 0 },
    { key: "must", label: "One rubric point marked “Must cover”", done: pts.some((p) => p.importance === "must") },
    { key: "marks", label: "Marks for this question", done: (q.marks || 0) > 0 },
  ];
}
function tipsOf(q, modelAnswer) {
  const t = [];
  q.flow.forEach((s) => s.rubric.forEach((p) => {
    if (/\sand\s/i.test(p.text)) t.push({ msg: `“${p.text}” covers two things — split it so each is graded on its own.`, action: `split:${s.id}:${p.id}`, part: s.id });
  }));
  q.flow.forEach((s) => {
    if (s.name.trim() && s.rubric.length === 0) t.push({ msg: `“${s.name}” has no rubric yet — the AI can't grade this part.`, part: s.id });
  });
  const pts = allPoints(q);
  if (pts.length > 0 && pts.length < 3) t.push({ msg: "Three or more rubric points across the flow give the AI enough to dig into." });
  const maWords = ((modelAnswer || "").toLowerCase().match(/[a-z]{6,}/g) || []).filter((w) => !STOP.has(w));
  const nudgeText = q.flow.map((s) => s.nudge).join(" ").toLowerCase();
  const leak = maWords.find((w) => nudgeText.includes(w));
  if (leak) t.push({ msg: `A nudge may give the answer away — “${leak}” also appears in the overall model answer.` });
  return t;
}

/* ── error boundary ───────────────────────────────────────── */
class Boundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) return (
      <div style={{ maxWidth: 560, margin: "60px auto", padding: 20, background: "rgba(248,113,113,0.12)", border: "1px solid rgba(248,113,113,0.35)", borderRadius: 12, fontFamily: "sans-serif" }}>
        <b>Something failed to render.</b>
        <div style={{ marginTop: 8, fontSize: 13, color: "#7C2C24" }}>{String(this.state.err)}</div>
      </div>
    );
    return this.props.children;
  }
}

/* ── main ──────────────────────────────────────────────────── */
function Builder({ onExit, onPublish, initial }) {
  const stInit = initial && initial.format !== "simple" ? initial.payload : null;
  const sInit = initial && initial.format === "simple" ? initial.payload : null;
  const [screen, setScreen] = useState(initial ? "workspace" : "setup");
  const [format, setFormat] = useState(initial?.format || "structured");
  const [meta, setMeta] = useState(() => ({ ...(initial?.meta || {}),
    title: "", description: "", role: "", seniority: "Senior", company: "", roundType: "Mixed",
    language: "English (India)", durationMin: 45, hardCap: 55,
    styleDesc: "neutral", pressure: 3,
    openingScript: "Hi! I'm your interviewer for this mock session. We'll work through a few questions — think out loud, and ask me anything you'd ask a real interviewer.",
    closingScript: "That's the end of our session — thank you! I won't share a verdict out loud; your detailed feedback card is on its way.",
    ...(initial?.meta || {}),
  }));
  const setM = (patch) => setMeta((m) => ({ ...m, ...patch }));
  const [name, setName] = useState(initial?.title || "My interview");

  /* structured format state */
  const [questions, setQuestions] = useState(() => { const m = {}; (stInit?.questions || []).forEach((q) => { m[q.id] = q; }); return m; });
  const [order, setOrder] = useState(() => (stInit?.questions || []).map((q) => q.id));
  const [activeQ, setActiveQ] = useState(stInit?.questions?.[0]?.id || null);
  const [modelAnswer, setModelAnswer] = useState(stInit?.model_answer || "");

  /* simple format state */
  const [sIntro, setSIntro] = useState(sInit?.intro || "");
  const [sQuestions, setSQuestions] = useState(sInit?.questions || []);
  const [sEnding, setSEnding] = useState(sInit?.ending || "");
  const [simplePreview, setSimplePreview] = useState(false);

  const [saveState, setSaveState] = useState("Saved");
  const [snack, setSnack] = useState(null);
  const [preview, setPreview] = useState(null);
  const [addPick, setAddPick] = useState(false);
  const [copied, setCopied] = useState({});

  const focusRef = useRef(null);
  const scrollRef = useRef(null);
  const secRefs = useRef({});

  const fmt = FORMATS.find((f) => f.id === format) || FORMATS[0];
  const q = activeQ ? questions[activeQ] : null;

  const bindFocus = (id) => (el) => {
    if (el && focusRef.current === id) {
      el.focus();
      try { const L = el.value.length; el.setSelectionRange(L, L); } catch {}
      focusRef.current = null;
    }
  };
  const bindSection = (id) => (el) => {
    secRefs.current[id] = el;
    if (el && scrollRef.current === id) {
      try { el.scrollIntoView({ behavior: "smooth", block: "start" }); } catch {}
      scrollRef.current = null;
    }
  };
  const jumpTo = (id) => {
    const qq = questions[activeQ];
    if (qq && (qq.flow || []).some((x) => x.id === id)) { setActivePartByQ((p) => ({ ...p, [activeQ]: id })); return; }
    const el = secRefs.current[id]; if (el) try { el.scrollIntoView({ behavior: "smooth", block: "start" }); } catch {}
  };

  const touch = () => { setSaveState("Saving…"); setTimeout(() => setSaveState("Saved"), 700); };
  const set = (patch) => { setQuestions((p) => ({ ...p, [activeQ]: { ...p[activeQ], ...patch } })); touch(); };
  const patchQ = (id, patch) => { setQuestions((p) => ({ ...p, [id]: { ...p[id], ...patch } })); touch(); };
  const [activePartByQ, setActivePartByQ] = useState({});   // parts render as tabs — one visible

  const setPart = (sid, patch) => set({ flow: q.flow.map((s) => (s.id === sid ? { ...s, ...patch } : s)) });
  const setPartItem = (sid, list, id, patch) => {
    const s = q.flow.find((x) => x.id === sid);
    setPart(sid, { [list]: s[list].map((x) => (x.id === id ? { ...x, ...patch } : x)) });
  };
  const rmPartItem = (sid, list, id) => {
    const s = q.flow.find((x) => x.id === sid);
    setPart(sid, { [list]: s[list].filter((x) => x.id !== id) });
  };
  const defImp = () => (allPoints(q).some((x) => x.importance === "must") ? "good" : "must");
  const ghostAddPoint = (sid, text) => {
    const np = newPoint(text, defImp());
    const s = q.flow.find((x) => x.id === sid);
    focusRef.current = np.id;
    setPart(sid, { rubric: [...s.rubric, np] });
  };
  const insertPointAfter = (sid, idx) => {
    const np = newPoint("", defImp());
    const s = q.flow.find((x) => x.id === sid);
    const c = [...s.rubric]; c.splice(idx + 1, 0, np);
    focusRef.current = np.id;
    setPart(sid, { rubric: c });
  };
  const ghostAddPitfall = (sid, text) => {
    const pf = { id: nid(), text, say: "" };
    const s = q.flow.find((x) => x.id === sid);
    focusRef.current = pf.id;
    setPart(sid, { pitfalls: [...s.pitfalls, pf] });
  };
  const addPart = () => {
    const p = newPart("");
    focusRef.current = "pn:" + p.id;
    scrollRef.current = p.id;
    set({ flow: [...q.flow, p] });
  };
  const rmPart = (sid) => set({ flow: q.flow.filter((s) => s.id !== sid) });
  const movePart = (sid, dir) => {
    const i = q.flow.indexOf(q.flow.find((s) => s.id === sid)); const j = i + dir;
    if (j < 0 || j >= q.flow.length) return;
    const c = [...q.flow]; [c[i], c[j]] = [c[j], c[i]]; set({ flow: c });
  };

  /* simple format handlers — same ghost-row ergonomics */
  const setSQ = (id, patch) => { setSQuestions((l) => l.map((x) => (x.id === id ? { ...x, ...patch } : x))); touch(); };
  const rmSQ = (id) => { setSQuestions((l) => l.filter((x) => x.id !== id)); touch(); };
  const moveSQ = (id, dir) => {
    setSQuestions((l) => { const i = l.findIndex((x) => x.id === id); const j = i + dir;
      if (j < 0 || j >= l.length) return l; const c = [...l]; [c[i], c[j]] = [c[j], c[i]]; return c; }); touch();
  };
  const ghostAddSQ = (text) => {
    const nq = { id: nid(), text, minutes: 2 };
    focusRef.current = nq.id;
    setSQuestions((l) => [...l, nq]); touch();
  };
  const insertSQAfter = (idx) => {
    const nq = { id: nid(), text: "", minutes: 2 };
    focusRef.current = nq.id;
    setSQuestions((l) => { const c = [...l]; c.splice(idx + 1, 0, nq); return c; }); touch();
  };

  const allNeeded = useMemo(() => {
    const o = {}; for (const id of order) o[id] = neededOf(questions[id]).filter((n) => !n.done); return o;
  }, [questions, order]);
  const allTips = useMemo(() => {
    const o = {}; for (const id of order) o[id] = tipsOf(questions[id], modelAnswer); return o;
  }, [questions, order, modelAnswer]);
  const readyCount = order.filter((id) => allNeeded[id].length === 0).length;
  const allReady = order.length > 0 && readyCount === order.length;
  const totalMin = order.reduce((a, id) => a + (questions[id].minutes || 0), 0);
  const totalMarks = order.reduce((a, id) => a + (questions[id].marks || 0), 0);
  const marksOk = totalMarks === 100;
  const modelOk = !!modelAnswer.trim();

  /* simple format derived */
  const sCount = sQuestions.filter((x) => x.text.trim()).length;
  const sEstMin = sQuestions.reduce((a, x) => a + (x.minutes || 0), 0) + 2;
  const sNeeds = [
    { key: "intro", label: "Introduction", done: !!sIntro.trim() },
    { key: "qs", label: "At least one question", done: sCount > 0 },
    { key: "end", label: "Ending", done: !!sEnding.trim() },
  ];
  const sBlockers = [
    !sIntro.trim() && "write the introduction",
    sCount === 0 && "add at least one question",
    !sEnding.trim() && "write the ending",
  ].filter(Boolean);

  const balanceMarks = () => {
    const ids = [...order]; if (!ids.length) return;
    const tot = ids.reduce((a, id) => a + (questions[id].marks || 0), 0);
    let alloc;
    if (tot <= 0) {
      const base = Math.floor(100 / ids.length);
      alloc = ids.map(() => base);
      alloc[0] += 100 - base * ids.length;
    } else {
      const raw = ids.map((id) => ((questions[id].marks || 0) * 100) / tot);
      const fl = raw.map(Math.floor);
      const rem = 100 - fl.reduce((a, b) => a + b, 0);
      const byFrac = raw.map((r, i) => [r - fl[i], i]).sort((a, b) => b[0] - a[0]);
      alloc = [...fl];
      for (let k = 0; k < rem; k++) alloc[byFrac[k % ids.length][1]] += 1;
    }
    setQuestions((p) => { const c = { ...p }; ids.forEach((id, i) => (c[id] = { ...c[id], marks: alloc[i] })); return c; });
    touch();
  };

  const publishToCommunity = (status = "published") => {
    try {
      onPublish?.({
        status,
        format,
        title: (meta.title || '').trim() || name,
        meta,
        payload: format === 'structured'
          ? { model_answer: modelAnswer, questions: order.map((id) => questions[id]).filter(Boolean) }
          : { intro: sIntro, questions: sQuestions, ending: sEnding },
      });
    } catch { /* publishing to community must never break the builder */ }
  };

  const go = (s) => { setScreen(s); try { window.scrollTo(0, 0); } catch {} };
  const openQ = (id, part = null) => { setActiveQ(id); scrollRef.current = part; go("workspace"); };

  const createInterview = () => {
    /* seed BOTH formats so switching later never loses work — instantly */
    const qs = DRAFT();
    const map = {}; qs.forEach((x) => (map[x.id] = x));
    setQuestions(map); setOrder(qs.map((x) => x.id));
    setModelAnswer(SEED_MODEL_ANSWER);
    setActiveQ(qs[0].id);
    setSIntro(meta.openingScript);
    setSQuestions(SDRAFT());
    setSEnding(meta.closingScript);
    setName(meta.title.trim() || "My interview");
    go("workspace"); touch();
  };

  const addQuestion = (t) => {
    const nq = typePrefill(t);
    setQuestions((p) => ({ ...p, [nq.id]: nq }));
    setOrder((o) => [...o, nq.id]);
    setAddPick(false); touch();
    setActiveQ(nq.id);
  };

  const move = (id, dir) => {
    setOrder((o) => { const i = o.indexOf(id), j = i + dir; if (j < 0 || j >= o.length) return o;
      const c = [...o]; [c[i], c[j]] = [c[j], c[i]]; return c; }); touch();
  };

  const removeQ = (id) => {
    const idx = order.indexOf(id);
    const remaining = order.filter((x) => x !== id);
    setOrder(remaining); touch();
    if (activeQ === id) setActiveQ(remaining[0] ?? null);
    setSnack({ msg: `“${titleOf(questions[id])}” deleted`, undo: () => {
      setOrder((o) => { const c = [...o]; c.splice(idx, 0, id); return c; }); setSnack(null);
    }});
    setTimeout(() => setSnack((s) => (s && s.msg.startsWith("“") ? null : s)), 6000);
  };

  const onTipAction = (t) => {
    if (t.action && t.action.startsWith("split:")) {
      const [, sid, pid] = t.action.split(":");
      const s = q.flow.find((x) => x.id === sid); if (!s) return;
      const p = s.rubric.find((x) => x.id === pid); if (!p) return;
      const [a, b] = p.text.split(/\sand\s/i);
      const mk = (txt) => ({ ...p, id: nid(), text: txt.trim().replace(/^./, (c) => c.toUpperCase()) });
      setPart(sid, { rubric: s.rubric.flatMap((x) => (x.id === pid ? [mk(a), mk(b)] : [x])) });
      jumpTo(sid);
    } else if (t.part) jumpTo(t.part);
  };

  const copyLink = (k) => { setCopied((c) => ({ ...c, [k]: true })); setTimeout(() => setCopied((c) => ({ ...c, [k]: false })), 1500); };


  /* ── setup ───────────────────────────────────────────────── */
  const Setup = (
    <div className="fadein" style={{ maxWidth: 1080, margin: "0 auto", padding: "26px 18px 60px", display: "grid", gap: 14 }}>
      <div>
        <div className="h1" style={{ fontSize: 30 }}>Set up your interview</div>
        <p className="sub" style={{ marginTop: 5, fontSize: 13.5 }}>Who this interview is for, who conducts it, and the format you'll author in.</p>
      </div>
      <div className="setupgrid">
        <div className="card" style={{ padding: 18, display: "grid", gap: 13 }}>
          <div><span className="secno">Section 1</span><div className="h2" style={{ marginTop: 4 }}>Interview basics</div></div>
          <div>
            <label className="label">Title</label>
            <input className="fld" placeholder="e.g. FDE Mock — Round 2" value={meta.title} onChange={(e) => setM({ title: e.target.value })} />
          </div>
          <div>
            <label className="label">Description — shown to learners before they start</label>
            <textarea className="fld" rows={2} placeholder="What this session covers and how to approach it." value={meta.description} onChange={(e) => setM({ description: e.target.value })} />
          </div>
          <div className="frow">
            <div>
              <label className="label">Target role</label>
              <input className="fld" placeholder="e.g. Forward Deployed Engineer" value={meta.role} onChange={(e) => setM({ role: e.target.value })} />
            </div>
            <div>
              <label className="label">Topic</label>
              <input className="fld" placeholder="e.g. System Design, Behavioral" value={meta.topic || ""} onChange={(e) => setM({ topic: e.target.value })} />
            </div>
            <div>
              <label className="label">Seniority</label>
              <select className="fld" value={meta.seniority} onChange={(e) => setM({ seniority: e.target.value })}>
                {["Junior", "Mid-level", "Senior", "Staff+"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="frow">
            <div>
              <label className="label">Company <span style={{ textTransform: "none", fontWeight: 500 }}>(optional)</span></label>
              <input className="fld" placeholder="e.g. Google" value={meta.company} onChange={(e) => setM({ company: e.target.value })} />
            </div>
            <div>
              <label className="label">Round type</label>
              <select className="fld" value={meta.roundType} onChange={(e) => setM({ roundType: e.target.value })}>
                {["Screening", "Behavioral", "System design", "Technical", "Mixed", "HR"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="frow">
            <div>
              <label className="label">Language</label>
              <select className="fld" value={meta.language} onChange={(e) => setM({ language: e.target.value })}>
                {["English (India)", "English (US)", "English (UK)", "Hindi", "Hinglish"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Target duration</label>
              <div className="row">
                <input className="fld" type="number" min={5} style={{ width: 78 }} value={meta.durationMin} onChange={(e) => setM({ durationMin: parseInt(e.target.value || 0) })} />
                <span className="sub">min</span>
              </div>
            </div>
            <div>
              <label className="label">Hard cap</label>
              <div className="row">
                <input className="fld" type="number" min={5} style={{ width: 78 }} value={meta.hardCap} onChange={(e) => setM({ hardCap: parseInt(e.target.value || 0) })} />
                <span className="sub">min</span>
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: 18, display: "grid", gap: 13 }}>
          <div><span className="secno">Section 2</span><div className="h2" style={{ marginTop: 4 }}>Interviewer persona</div></div>
          <div className="frow">
            <div>
              <label className="label">Style</label>
              <div className="seg" role="group">
                {[["warm", "Warm"], ["neutral", "Neutral"], ["terse", "Terse & probing"]].map(([v, l]) => (
                  <button key={v} className={meta.styleDesc === v ? "on" : ""} onClick={() => setM({ styleDesc: v })}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="label">Pressure — {meta.pressure} · {PRESSURE[meta.pressure]}</label>
              <input type="range" min="1" max="5" step="1" style={{ width: "100%", accentColor: "var(--acc)" }} value={meta.pressure} onChange={(e) => setM({ pressure: parseInt(e.target.value) })} aria-label="Pressure dial" />
              <div className="row" style={{ justifyContent: "space-between" }}>
                <span className="sub">1 · Friendly</span><span className="sub">5 · Bar-raiser</span>
              </div>
            </div>
          </div>
          <div>
            <label className="label">Opening script — greeting & expectation-setting</label>
            <textarea className="fld" rows={2} value={meta.openingScript} onChange={(e) => setM({ openingScript: e.target.value })} />
          </div>
          <div>
            <label className="label">Closing script — no verdict aloud; what happens next</label>
            <textarea className="fld" rows={2} value={meta.closingScript} onChange={(e) => setM({ closingScript: e.target.value })} />
          </div>
          <div className="row">
            <button className={"chip" + (meta.smallTalk ? " on" : "")} onClick={() => setM({ smallTalk: !meta.smallTalk })}>Warm-up small talk before the first question</button>
          </div>
        </div>
      </div>

      {/* Section 3 — the format catalog */}
      <div className="card" style={{ padding: 18, display: "grid", gap: 12 }}>
        <div>
          <span className="secno">Section 3</span>
          <div className="h2" style={{ marginTop: 4 }}>Interview format</div>
          <p className="sub" style={{ marginTop: 3 }}>Each format is a different schema you type into — and a different way the backend voice agent is tuned. Pick one; you can switch later from “Edit setup” without losing work.</p>
        </div>
        <div className="fmtgrid" role="radiogroup" aria-label="Interview format">
          {FORMATS.map((f, fi) => (
            <button key={f.id} className={"fmt" + (format === f.id ? " on" : "") + (f.soon ? " soon" : "")}
              disabled={!!f.soon} role="radio" aria-checked={format === f.id}
              onClick={() => !f.soon && setFormat(f.id)}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <span className="fmtno">{String(fi + 1).padStart(2, "0")}</span>
                <span className={"status " + (format === f.id ? "st-ok" : "st-need")} style={f.soon ? { background: "var(--bg)", color: "var(--ink3)", borderColor: "var(--line2)" } : {}}>{f.tag}</span>
              </div>
              <span className="fmtname">{f.name}</span>
              <p className="sub">{f.desc}</p>
              {!f.soon && (
                <>
                  <p className="sub" style={{ fontSize: 11.5 }}><b>Voice agent:</b> {f.agent}</p>
                  <p className="sub" style={{ fontSize: 11.5 }}><b>Report:</b> {f.report}</p>
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="row" style={{ justifyContent: "flex-end" }}>
        <button className="btn btn-acc" disabled={!meta.title.trim()} title={!meta.title.trim() ? "Give the interview a title first" : ""} onClick={createInterview}>Create interview — {fmt.name.toLowerCase()} →</button>
      </div>
    </div>
  );



  /* ── structured workspace ────────────────────────────────── */
  const QuestionRail = (
    <nav className="rail pane" aria-label="Questions">
      <div className="rail-h">
        <div className="label" style={{ marginBottom: 2 }}>Questions</div>
        <div className="sub">
          {readyCount}/{order.length} ready · {totalMin + 2}/{meta.durationMin} min ·{" "}
          <span style={{ color: marksOk ? "var(--ok)" : "var(--need)", fontWeight: 600 }}>{totalMarks}/100 pts</span>
        </div>
        {!marksOk && <button className="btn" style={{ marginTop: 6, padding: "3px 10px", fontSize: 12 }} onClick={balanceMarks}>Auto-balance to 100</button>}
      </div>
      <div className="rail-b">
        {order.map((id, i) => {
          const qq = questions[id];
          const need = allNeeded[id].length;
          return (
            <div key={id} className={"qrow" + (id === activeQ ? " on" : "")} role="button" tabIndex={0}
              onClick={() => setActiveQ(id)}
              onKeyDown={(e) => e.key === "Enter" && setActiveQ(id)}>
              <span className="dot" style={{ background: need ? "var(--need)" : "var(--ok)" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i + 1}. {titleOf(qq)}</div>
                <div className="sub qmeta" onClick={(e) => e.stopPropagation()}>
                  {TYPES[qq.type]} ·
                  <input className="fld mini" type="number" min={1} aria-label="Minutes" value={qq.minutes}
                    onChange={(e) => patchQ(id, { minutes: parseInt(e.target.value || 0) })} />m ·
                  <input className="fld mini" type="number" min={0} max={100} aria-label="Marks" value={qq.marks || 0}
                    onChange={(e) => patchQ(id, { marks: parseInt(e.target.value || 0) })} /> pts
                  · {qq.flow.length} parts{need ? ` · ${need} needed` : ""}
                </div>
              </div>
              <span className="ops" onClick={(e) => e.stopPropagation()}>
                <button className="btn-ico" aria-label="Move up" disabled={i === 0} onClick={() => move(id, -1)}>↑</button>
                <button className="btn-ico" aria-label="Move down" disabled={i === order.length - 1} onClick={() => move(id, 1)}>↓</button>
                <button className="btn-ico" aria-label="Delete question" onClick={() => removeQ(id)}>✕</button>
              </span>
            </div>
          );
        })}
      </div>
      <div className="rail-f">
        {!addPick ? (
          <button className="btn" style={{ width: "100%" }} onClick={() => setAddPick(true)}>+ Add question</button>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            <div className="label">Type</div>
            <div className="row" style={{ flexWrap: "wrap" }}>
              {Object.entries(TYPES).map(([k, v]) => (
                <button key={k} className="chip" onClick={() => addQuestion(k)}>{v}</button>
              ))}
            </div>
            <button className="btn-ghost" style={{ justifySelf: "start" }} onClick={() => setAddPick(false)}>Cancel</button>
          </div>
        )}
      </div>
    </nav>
  );

  const Editor = q ? (() => {
    const need = neededOf(q);
    const open_ = need.filter((n) => !n.done).length;
    const tips = allTips[activeQ] || [];
    const idx = order.indexOf(activeQ);
    const selPart = activePartByQ[activeQ];
    const curPart = selPart === "__last" ? q.flow[q.flow.length - 1]
      : (q.flow.find((x) => x.id === selPart) || q.flow[0]);
    return (
      <div className="dwrap fadein">
        <div className="sub" style={{ fontWeight: 600, color: "var(--ink)" }}>Q{idx + 1} of {order.length}</div>

        {/* readiness */}
        <div className="card" style={{ padding: 12, borderColor: open_ === 0 ? "var(--ok-line)" : "var(--line)", background: open_ === 0 ? "var(--ok-bg)" : "var(--card)" }}>
          {open_ === 0 ? (
            <div className="check" style={{ color: "var(--ok)", fontWeight: 600 }}>
              <span className="ckdot done">✓</span> This question is ready.
            </div>
          ) : (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {need.map((n) => (
                <div key={n.key} className="check" style={{ color: n.done ? "var(--ink3)" : "var(--ink)" }}>
                  <span className={"ckdot" + (n.done ? " done" : "")}>{n.done ? "✓" : ""}</span>
                  <span style={n.done ? { textDecoration: "line-through" } : {}}>{n.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* the question */}
        <div className="card" style={{ padding: 16, display: "grid", gap: 12 }} ref={bindSection("top")}>
          <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
            <span className="secno">The question</span>
            <span className="chip static">{TYPES[q.type]}</span>
          </div>
          <input className="fld" placeholder="Heading — a short label for the question list (e.g. Project deep-dive)"
            value={q.heading || ""} onChange={(e) => set({ heading: e.target.value })} />
          <textarea className="fld" rows={2} placeholder="Asked exactly as written — e.g. Tell me about a time you had to change a client's mind." value={q.prompt} onChange={(e) => set({ prompt: e.target.value })} />
        </div>

        {/* the whole conversation, inline */}
        <div className="card" style={{ padding: "14px 16px 16px", display: "grid", gap: 4 }}>
          <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
            <span className="secno">Conversation & rubric — everything inline</span>
            <span className="sub">Type in the last row to add a point · Enter inserts the next row</span>
          </div>

          {/* sticky part navigator */}
          <div className="pnav">
            {q.flow.map((s, i) => (
              <button key={s.id} className={"chip" + (curPart && s.id === curPart.id ? " on" : "")}
                onClick={() => jumpTo(s.id)}>{i + 1} · {(s.name || "Untitled").slice(0, 18)}</button>
            ))}
            <button className="chip" style={{ color: "var(--acc)", fontWeight: 600 }}
              onClick={() => { addPart(); setActivePartByQ((p) => ({ ...p, [activeQ]: "__last" })); }}>+ Part</button>
          </div>

          {q.flow.map((s, i) => ({ s, i })).filter(({ s }) => !curPart || s.id === curPart.id).map(({ s, i }) => (
            <div key={s.id} className="psec" ref={bindSection(s.id)}>
              {/* part header */}
              <div className="row" style={{ flexWrap: "wrap" }}>
                <span className="stg-num">{i + 1}</span>
                <input className="pname" placeholder="Name this part of the conversation" value={s.name}
                  ref={bindFocus("pn:" + s.id)}
                  onChange={(e) => setPart(s.id, { name: e.target.value })} />
                <button className={"chip" + (s.must ? " on" : "")} title="Agent won't move on until this part is covered"
                  onClick={() => setPart(s.id, { must: !s.must })}>{s.must ? "✓ must cover" : "must cover"}</button>
                <span className="row" style={{ flex: "none" }}>
                  <button className="btn-ico" aria-label="Move part up" disabled={i === 0} onClick={() => movePart(s.id, -1)}>↑</button>
                  <button className="btn-ico" aria-label="Move part down" disabled={i === q.flow.length - 1} onClick={() => movePart(s.id, 1)}>↓</button>
                  <button className="btn-ico" aria-label="Remove part" onClick={() => rmPart(s.id)}>✕</button>
                </span>
              </div>

              {/* rubric table with ghost row */}
              <div className="rt-scroll">
                <div className="rt-grid" role="table" aria-label={"Rubric — " + (s.name || "part " + (i + 1))}>
                  <div className="rt-h">Rubric point</div>
                  <div className="rt-h">Answer remark — what the AI listens for</div>
                  <div className="rt-h">Importance</div>
                  <div className="rt-h" aria-hidden></div>
                  {s.rubric.map((p, k) => {
                    const accBar = p.importance === "must" ? { boxShadow: "inset 3px 0 0 var(--pop)" } : {};
                    const bb = "1px solid var(--line)";
                    return (
                      <div key={p.id} style={{ display: "contents" }}>
                        <div className="rt-c" style={{ borderBottom: bb, ...accBar }}>
                          <textarea rows={1} onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} className="rt-in" style={{ fontWeight: 600 }} placeholder="One thing a great answer includes here"
                            value={p.text} ref={bindFocus(p.id)}
                            onChange={(e) => setPartItem(s.id, "rubric", p.id, { text: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); insertPointAfter(s.id, k); } }}
                            aria-label="Rubric point" />
                        </div>
                        <div className="rt-c" style={{ borderBottom: bb }}>
                          <textarea rows={1} onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} className="rt-in" placeholder="What should the AI listen for?" value={p.remark} onChange={(e) => setPartItem(s.id, "rubric", p.id, { remark: e.target.value })} aria-label="Answer remark" />
                        </div>
                        <div className="rt-c" style={{ borderBottom: bb }}>
                          <select className="rt-in" value={p.importance} onChange={(e) => setPartItem(s.id, "rubric", p.id, { importance: e.target.value })} aria-label="Importance">
                            <option value="must">Must cover</option>
                            <option value="good">Good to have</option>
                            <option value="bonus">Bonus</option>
                          </select>
                        </div>
                        <div className="rt-c" style={{ borderBottom: bb, justifyContent: "center" }}>
                          <button className="btn-ico" aria-label="Remove rubric point" onClick={() => rmPartItem(s.id, "rubric", p.id)}>✕</button>
                        </div>
                      </div>
                    );
                  })}
                  {/* ghost row — type to add */}
                  <div style={{ display: "contents" }}>
                    <div className="rt-c">
                      <input className="rt-in ghost-in" placeholder="+ Type to add a rubric point…" value=""
                        onChange={(e) => ghostAddPoint(s.id, e.target.value)} aria-label="Add rubric point" />
                    </div>
                    <div className="rt-c" aria-hidden></div>
                    <div className="rt-c" aria-hidden></div>
                    <div className="rt-c" aria-hidden></div>
                  </div>
                </div>
              </div>

              {/* pitfalls: existing rows + ghost */}
              {s.pitfalls.map((pf) => (
                <div key={pf.id} className="row" style={{ flexWrap: "wrap" }}>
                  <span className="sub" style={{ flex: "none", width: 52, color: "var(--need)", fontWeight: 600 }}>Pitfall</span>
                  <input className="fld" style={{ flex: 1, minWidth: 160 }} placeholder="The pitfall, in one line" value={pf.text}
                    ref={bindFocus(pf.id)}
                    onChange={(e) => setPartItem(s.id, "pitfalls", pf.id, { text: e.target.value })} />
                  <span className="sub" style={{ flex: "none" }}>→</span>
                  <input className="fld" style={{ flex: 1.4, minWidth: 180 }} placeholder="If it happens, the AI says… (practice)" value={pf.say} onChange={(e) => setPartItem(s.id, "pitfalls", pf.id, { say: e.target.value })} />
                  <button className="btn-ico" aria-label="Remove pitfall" onClick={() => rmPartItem(s.id, "pitfalls", pf.id)}>✕</button>
                </div>
              ))}
              <div className="row">
                <span className="sub" style={{ flex: "none", width: 52 }}></span>
                <input className="fld ghost-in" style={{ flex: 1, borderStyle: "dashed" }} placeholder="+ Type to add a pitfall for this part…" value=""
                  onChange={(e) => ghostAddPitfall(s.id, e.target.value)} aria-label="Add pitfall" />
              </div>

              {/* nudge + curveball inline */}
              <div className="frow">
                <div>
                  <label className="label">Nudge if stuck <span style={{ textTransform: "none", fontWeight: 500 }}>(practice only)</span></label>
                  <input className="fld" placeholder="A small push in your words" value={s.nudge} onChange={(e) => setPart(s.id, { nudge: e.target.value })} />
                </div>
                <div>
                  <label className="label">Curveball after this part <span style={{ textTransform: "none", fontWeight: 500 }}>(optional)</span></label>
                  <input className="fld" placeholder="e.g. The budget just got cut in half — what changes?" value={s.curveball} onChange={(e) => setPart(s.id, { curveball: e.target.value })} />
                </div>
              </div>
            </div>
          ))}

          <div className="psec" style={{ paddingBottom: 4 }}>
            <button className="btn" style={{ justifySelf: "start" }} onClick={addPart}>+ Add a part to the flow</button>
          </div>
        </div>

        {/* tips */}
        {tips.length > 0 && (
          <div style={{ display: "grid", gap: 6 }}>
            {tips.map((t, k) => (
              <div key={k} className="tip"><span style={{ fontWeight: 700 }}>◦</span><span style={{ flex: 1 }}>{t.msg}</span>
                {(t.action || t.part) && (
                  <button className="btn" style={{ padding: "3px 10px", fontSize: 12, flex: "none" }} onClick={() => onTipAction(t)}>
                    {t.action ? "Split it" : "Show →"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* overall model answer — interview-level */}
        <div className="card" style={{ padding: 16, display: "grid", gap: 8, borderColor: "var(--acc-line)" }}>
          <span className="secno">Overall model answer — whole interview</span>
          <p className="sub">One answer guide for the entire interview. It appears in every learner's feedback; the AI never says it aloud. It stays the same no matter which question you're editing.</p>
          <textarea className="fld" rows={3} placeholder="What separates a great performance across this whole interview — write it like you'd explain it to a student."
            value={modelAnswer} onChange={(e) => { setModelAnswer(e.target.value); touch(); }} />
        </div>
      </div>
    );
  })() : null;

  const Workspace = (
    <div className="ws2 fadein">
      {QuestionRail}
      {order.length === 0 ? (
        <div className="detail pane" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ padding: 26, textAlign: "center", display: "grid", gap: 10, maxWidth: 380 }}>
            <div className="h2">No questions yet</div>
            <p className="sub">Add your first question — its type pre-fills a typical conversation flow.</p>
            <button className="btn btn-acc" onClick={() => setAddPick(true)}>+ Add a question</button>
          </div>
        </div>
      ) : (
        <div className="detail pane">{Editor}</div>
      )}
    </div>
  );

  /* ── simple workspace — one page: intro → questions → ending ── */
  const SimpleWorkspace = (
    <div className="fadein" style={{ maxWidth: 780, margin: "0 auto", padding: "18px 18px 60px", display: "grid", gap: 12 }}>
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
        <span className="sub mono" style={{ fontWeight: 600, color: "var(--ink)" }}>Simple script · {sCount} questions · ~{sEstMin} min</span>
      </div>

      {/* readiness */}
      <div className="card" style={{ padding: 12, borderColor: sBlockers.length === 0 ? "var(--ok-line)" : "var(--line)", background: sBlockers.length === 0 ? "var(--ok-bg)" : "var(--card)" }}>
        {sBlockers.length === 0 ? (
          <div className="check" style={{ color: "var(--ok)", fontWeight: 600 }}>
            <span className="ckdot done">✓</span> The script is ready.
          </div>
        ) : (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {sNeeds.map((n) => (
              <div key={n.key} className="check" style={{ color: n.done ? "var(--ink3)" : "var(--ink)" }}>
                <span className={"ckdot" + (n.done ? " done" : "")}>{n.done ? "✓" : ""}</span>
                <span style={n.done ? { textDecoration: "line-through" } : {}}>{n.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* introduction */}
      <div className="card" style={{ padding: 16, display: "grid", gap: 8 }}>
        <span className="secno">Introduction — how the AI opens</span>
        <textarea className="fld" rows={2} placeholder="e.g. Hi! Thanks for making time. We'll go through a few quick questions — relax and answer naturally."
          value={sIntro} onChange={(e) => { setSIntro(e.target.value); touch(); }} />
      </div>

      {/* questions */}
      <div className="card" style={{ padding: 16, display: "grid", gap: 8 }}>
        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
          <span className="secno">Questions — asked in order, exactly as written</span>
          <span className="sub">Type in the last row to add · Enter inserts the next</span>
        </div>
        {sQuestions.map((x, i) => (
          <div key={x.id} className="row" style={{ flexWrap: "wrap" }}>
            <span className="stg-num">{i + 1}</span>
            <input className="fld" style={{ flex: 1, minWidth: 220 }} placeholder="Ask it exactly as written" value={x.text}
              ref={bindFocus(x.id)}
              onChange={(e) => setSQ(x.id, { text: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); insertSQAfter(i); } }} />
            <input className="fld" type="number" min={1} style={{ width: 62 }} value={x.minutes} onChange={(e) => setSQ(x.id, { minutes: parseInt(e.target.value || 0) })} aria-label="Minutes" />
            <span className="sub" style={{ flex: "none" }}>min</span>
            <span className="row" style={{ flex: "none" }}>
              <button className="btn-ico" aria-label="Move question up" disabled={i === 0} onClick={() => moveSQ(x.id, -1)}>↑</button>
              <button className="btn-ico" aria-label="Move question down" disabled={i === sQuestions.length - 1} onClick={() => moveSQ(x.id, 1)}>↓</button>
              <button className="btn-ico" aria-label="Remove question" onClick={() => rmSQ(x.id)}>✕</button>
            </span>
          </div>
        ))}
        <div className="row">
          <span className="stg-num" style={{ opacity: 0.4 }}>{sQuestions.length + 1}</span>
          <input className="fld ghost-in" style={{ flex: 1, borderStyle: "dashed" }} placeholder="+ Type to add a question…" value=""
            onChange={(e) => ghostAddSQ(e.target.value)} aria-label="Add question" />
        </div>
      </div>

      {/* ending */}
      <div className="card" style={{ padding: 16, display: "grid", gap: 8 }}>
        <span className="secno">Ending — how the AI wraps up</span>
        <textarea className="fld" rows={2} placeholder="e.g. That's everything from my side — thank you! You'll get your transcript and a short summary shortly."
          value={sEnding} onChange={(e) => { setSEnding(e.target.value); touch(); }} />
      </div>

      <p className="sub">In this format the agent asks your questions in order and keeps the session moving — no probing, no scores. Learners get the transcript plus a short AI summary.</p>
    </div>
  );

  const publishBlockers = [
    !allReady && "finish every question's checklist",
    !marksOk && `balance marks to 100 (now ${totalMarks})`,
    !modelOk && "add the overall model answer",
  ].filter(Boolean);

  const Review = (
    <div className="fadein" style={{ maxWidth: 1080, margin: "0 auto", padding: "22px 18px 60px", display: "grid", gap: 12 }}>
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
        <button className="btn-ghost" onClick={() => go("workspace")}>← Back to the editor</button>
      </div>
      <div className="revgrid">
        <div className="card span-all" style={{ padding: 18 }}>
          <div className="h1">{publishBlockers.length === 0 ? "Everything's ready" : `${readyCount} of ${order.length} questions ready`}</div>
          <p className="sub" style={{ marginTop: 4 }}>
            {publishBlockers.length === 0 ? "Take one test run so you hear exactly what your learners will hear — then publish." : "Finish the items below. Tips are optional — they just make the interview sharper."}
          </p>
        </div>

        {!marksOk && (
          <div className="card span-all" style={{ padding: 14 }}>
            <div className="tip miss" style={{ alignItems: "center" }}>
              <span style={{ fontWeight: 700 }}>!</span>
              <span style={{ flex: 1 }}>Marks add up to <b>{totalMarks}</b>, not 100. Adjust each question's marks, or rescale everything proportionally.</span>
              <button className="btn" style={{ flex: "none" }} onClick={balanceMarks}>Auto-balance to 100</button>
            </div>
          </div>
        )}
        {!modelOk && (
          <div className="card span-all" style={{ padding: 14 }}>
            <div className="tip miss" style={{ alignItems: "center" }}>
              <span style={{ fontWeight: 700 }}>!</span>
              <span style={{ flex: 1 }}>The overall model answer is empty — learners see it in every feedback card.</span>
              <button className="btn" style={{ flex: "none" }} onClick={() => go("workspace")}>Add it →</button>
            </div>
          </div>
        )}

        {order.map((id, i) => {
          const qq = questions[id];
          const need = allNeeded[id]; const tips = allTips[id];
          return (
            <div key={id} className="card" style={{ padding: 14, display: "grid", gap: 8, alignContent: "start" }}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{i + 1}. {titleOf(qq)}</div>
                <span className={"status " + (need.length ? "st-need" : "st-ok")}>{need.length ? `${need.length} needed` : "Ready"}</span>
              </div>
              <div className="sub mono">{qq.minutes} min · <b>{qq.marks || 0} pts</b> · {qq.flow.length}-part flow</div>
              {need.map((n) => (
                <div key={n.key} className="tip miss"><span style={{ fontWeight: 700 }}>!</span><span style={{ flex: 1 }}>Missing: {n.label.toLowerCase()}</span>
                  <button className="btn" style={{ padding: "3px 10px", fontSize: 12, flex: "none" }} onClick={() => openQ(id)}>Open →</button></div>
              ))}
              {tips.map((t, k) => (
                <div key={k} className="tip"><span style={{ fontWeight: 700 }}>◦</span><span style={{ flex: 1 }}>{t.msg}</span>
                  <button className="btn" style={{ padding: "3px 10px", fontSize: 12, flex: "none" }} onClick={() => openQ(id, t.part || null)}>Open →</button></div>
              ))}
              {need.length === 0 && tips.length === 0 && <p className="sub">Nothing to flag.</p>}
            </div>
          );
        })}
        <div className="card span-all" style={{ padding: 18, display: "grid", gap: 10 }}>
          <div className="h2">Before it goes to students</div>
          <p className="sub">A test run walks the whole flow out loud with your persona — {meta.styleDesc}, pressure {meta.pressure}/5. Scores land out of 100: <span className="mono">{order.map((id, i) => `Q${i + 1} ${questions[id].marks || 0}`).join(" · ")}</span>.</p>
          <div className="row">
            <button className="btn" disabled={!order.length} onClick={() => order.length && setPreview({ qid: order[0], tab: "feedback" })}>Preview the feedback report</button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-acc" disabled={publishBlockers.length > 0} title={publishBlockers.length ? "To publish: " + publishBlockers.join("; ") : ""} onClick={() => { publishToCommunity(); go("share"); }}>
              {publishBlockers.length === 0 ? "Publish interview" : "Publish — " + publishBlockers[0]}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const SimpleReview = (
    <div className="fadein" style={{ maxWidth: 780, margin: "0 auto", padding: "22px 18px 60px", display: "grid", gap: 12 }}>
      <div className="row">
        <button className="btn-ghost" onClick={() => go("workspace")}>← Back to the editor</button>
      </div>
      <div className="card" style={{ padding: 18 }}>
        <div className="h1">{sBlockers.length === 0 ? "The script is ready" : "A few things left"}</div>
        <p className="sub" style={{ marginTop: 4 }}>
          {sBlockers.length === 0 ? `~${sEstMin} minutes end to end. Review below, then publish.` : "Finish the items below, then publish."}
        </p>
      </div>
      {sBlockers.length > 0 && (
        <div className="card" style={{ padding: 14, display: "grid", gap: 8 }}>
          {sBlockers.map((b, i) => (
            <div key={i} className="tip miss" style={{ alignItems: "center" }}>
              <span style={{ fontWeight: 700 }}>!</span>
              <span style={{ flex: 1 }}>To publish: {b}.</span>
              <button className="btn" style={{ flex: "none" }} onClick={() => go("workspace")}>Open →</button>
            </div>
          ))}
        </div>
      )}
      <div className="card" style={{ padding: 16, display: "grid", gap: 8 }}>
        <span className="secno">The script</span>
        <div className="check"><span className={"ckdot" + (sIntro.trim() ? " done" : "")}>{sIntro.trim() ? "✓" : ""}</span><span className="sub" style={{ color: "var(--ink)" }}>Introduction</span></div>
        {sQuestions.filter((x) => x.text.trim()).map((x, i) => (
          <div key={x.id} className="check">
            <span className="stg-num" style={{ width: 20, height: 20, fontSize: 10 }}>{i + 1}</span>
            <span style={{ flex: 1 }}>{x.text}</span>
            <span className="sub mono" style={{ flex: "none" }}>{x.minutes}m</span>
          </div>
        ))}
        <div className="check"><span className={"ckdot" + (sEnding.trim() ? " done" : "")}>{sEnding.trim() ? "✓" : ""}</span><span className="sub" style={{ color: "var(--ink)" }}>Ending</span></div>
      </div>
      <div className="card" style={{ padding: 18, display: "grid", gap: 10 }}>
        <div className="h2">Before it goes out</div>
        <p className="sub">The agent asks in order with your persona — {meta.styleDesc}, pressure {meta.pressure}/5. Learners get the transcript plus a short AI summary; no scores in this format.</p>
        <div className="row">
          <div style={{ flex: 1 }} />
          <button className="btn btn-acc" disabled={sBlockers.length > 0} title={sBlockers.length ? "To publish: " + sBlockers.join("; ") : ""} onClick={() => { publishToCommunity(); go("share"); }}>
            {sBlockers.length === 0 ? "Publish interview" : "Publish — " + sBlockers[0]}
          </button>
        </div>
      </div>
    </div>
  );

  const Share = (
    <div className="fadein" style={{ maxWidth: 900, margin: "0 auto", padding: "22px 18px 60px", display: "grid", gap: 12 }}>
      <div className="card" style={{ padding: 22, textAlign: "center" }}>
        <div className="h1" style={{ fontSize: 30 }}>Published 🎉</div>
        <p className="sub" style={{ marginTop: 5 }}>“{name}” is live. Choose how each group of learners takes it — same interview, two doors.</p>
        <p className="sub mono" style={{ marginTop: 7, fontSize: 11.5 }}>Format: {fmt.name} · {meta.styleDesc} · pressure {meta.pressure}/5{format === "structured" ? " · scored /100" : " · transcript + summary"}</p>
      </div>
      <div className="setupgrid">
        {[
          { k: "practice", t: "Practice link", d: format === "structured" ? "Nudges allowed when someone's stuck. Feedback shows what they reached on their own vs with help." : "Relaxed pacing; the agent can rephrase a question once if someone's lost.", url: "roundtable.app/i/fde-mock-r2?m=practice" },
          { k: "exam", t: "Exam link", d: format === "structured" ? "No nudges, real pressure. Pitfalls are noted silently for the report." : "One pass, no rephrasing — closest to the real thing.", url: "roundtable.app/i/fde-mock-r2?m=exam" },
        ].map((l) => (
          <div key={l.k} className="card" style={{ padding: 16, display: "grid", gap: 8, alignContent: "start" }}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div className="h2">{l.t}</div>
              <button className="btn" onClick={() => copyLink(l.k)}>{copied[l.k] ? "Copied ✓" : "Copy link"}</button>
            </div>
            <p className="sub">{l.d}</p>
            <div className="fld mono" style={{ background: "var(--bg)", color: "var(--ink2)", fontSize: 12 }}>{l.url}</div>
          </div>
        ))}
      </div>
      <button className="btn-ghost" style={{ justifySelf: "center" }} onClick={() => go("workspace")}>← Back to the editor</button>
    </div>
  );

  /* ── structured preview modal ────────────────────────────── */
  const Preview = preview ? (() => {
    const pq = questions[preview.qid];
    if (!pq) return null;
    const lc = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
    const firstMustPart = pq.flow.find((s) => s.rubric.some((p) => p.importance === "must"));
    const firstMust = firstMustPart && firstMustPart.rubric.find((p) => p.importance === "must");
    const nextPart = firstMustPart && pq.flow[pq.flow.indexOf(firstMustPart) + 1];
    const curvePart = pq.flow.find((s) => s.curveball);
    const nudgePart = pq.flow.find((s) => s.nudge);
    const pitPart = pq.flow.find((s) => s.pitfalls.length > 0 && s.pitfalls[0].text);
    const goodPts = allPoints(pq).filter((p) => p.importance === "good");
    const missGood = goodPts[0];
    return (
      <div className="overlay" onClick={() => setPreview(null)}>
        <div className="modal fadein" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Question preview">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <div className="h2">{titleOf(pq)}</div>
            <button className="btn-ico" aria-label="Close preview" onClick={() => setPreview(null)}>✕</button>
          </div>
          <div className="seg" role="group">
            {[["feedback", "Feedback preview"]].map(([v, l]) => (
              <button key={v} className={preview.tab === v ? "on" : ""} onClick={() => setPreview({ ...preview, tab: v })}>{l}</button>
            ))}
          </div>

          {preview.tab === "hear" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p className="sub">The AI walks your {pq.flow.length}-part flow in order — {meta.styleDesc} style, pressure {meta.pressure}/5. Worth {pq.marks || 0} of 100 pts. A taste:</p>
              {preview.qid === order[0] && meta.openingScript && <div className="bubble bub-a">{meta.openingScript}</div>}
              <div className="bubble bub-a">{pq.prompt}</div>
              {firstMustPart && (
                <>
                  <p className="sub">In “{firstMustPart.name}”:</p>
                  <div className="bubble bub-l">(the learner does fine — but skips: {lc(firstMust.text)})</div>
                  <div className="bubble bub-a">Before we move on — {lc(firstMust.text).replace(/\.$/, "")}. Walk me through that?</div>
                </>
              )}
              {pitPart && (
                <>
                  <p className="sub">If they hit the pitfall in “{pitPart.name}” — {lc(pitPart.pitfalls[0].text)} (practice sessions):</p>
                  <div className="bubble bub-a">{pitPart.pitfalls[0].say || "Let's pause on that for a second."}</div>
                </>
              )}
              {nextPart && <div className="bubble bub-a">Good. Let's talk about {lc(nextPart.name)}.</div>}
              {curvePart && (<><p className="sub">After “{curvePart.name}”, your curveball:</p><div className="bubble bub-a">{curvePart.curveball}</div></>)}
              {nudgePart && (<><p className="sub">If they're stuck in “{nudgePart.name}” (practice sessions only):</p><div className="bubble bub-a">{nudgePart.nudge}</div></>)}
              <p className="sub" style={{ marginTop: 4 }}>Full test runs speak this out loud with your interviewer's voice.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              <p className="sub">What the learner sees right after this question — organized by your flow:</p>
              <div className="card" style={{ padding: 14, display: "grid", gap: 10, background: "var(--bg)", boxShadow: "none" }}>
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <div style={{ fontWeight: 700 }}>How you did — {titleOf(pq)}</div>
                  <span className="status st-ok">{Math.max(0, (pq.marks || 0) - 3)}/{pq.marks || 0} pts</span>
                </div>
                {pq.flow.map((s2, i2) => s2.rubric.length > 0 && (
                  <div key={s2.id} style={{ display: "grid", gap: 6 }}>
                    <div className="sub" style={{ fontWeight: 600, color: "var(--ink)" }}>{i2 + 1}. {s2.name}</div>
                    {s2.rubric.map((p) => (
                      <div key={p.id} className="check">
                        <span className="ckdot" style={p === missGood ? { background: "var(--need)", borderColor: "var(--need)" } : { background: "var(--ok)", borderColor: "var(--ok)" }}>
                          {p === missGood ? "!" : "✓"}
                        </span>
                        <span style={{ flex: 1 }}>{p.text}</span>
                        <span className="sub" style={{ flex: "none" }}>{p === missGood ? "missed" : "covered"}</span>
                      </div>
                    ))}
                  </div>
                ))}
                {modelAnswer && (
                  <div style={{ borderLeft: "3px solid var(--acc-line)", paddingLeft: 10 }}>
                    <div className="sub" style={{ fontWeight: 600, color: "var(--ink)" }}>What a great performance sounds like — overall</div>
                    <p className="sub">{modelAnswer}</p>
                  </div>
                )}
                {missGood && <p className="sub"><b>Try next:</b> {missGood.remark || missGood.text}</p>}
              </div>
              <p className="sub">Built automatically from each part's rubric, this question's marks, and the overall model answer.</p>
            </div>
          )}
        </div>
      </div>
    );
  })() : null;

  /* ── simple preview modal ────────────────────────────────── */
  const SimplePreviewModal = simplePreview ? (
    <div className="overlay" onClick={() => setSimplePreview(false)}>
      <div className="modal fadein" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Script preview">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div className="h2">Hear the script</div>
          <button className="btn-ico" aria-label="Close preview" onClick={() => setSimplePreview(false)}>✕</button>
        </div>
        <p className="sub">The agent runs your script top to bottom — {meta.styleDesc} style, pressure {meta.pressure}/5, ~{sEstMin} min.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {sIntro.trim() && <div className="bubble bub-a">{sIntro}</div>}
          {sQuestions.filter((x) => x.text.trim()).slice(0, 3).map((x) => (
            <div key={x.id} className="bubble bub-a">{x.text}</div>
          ))}
          {sCount > 3 && <p className="sub" style={{ alignSelf: "flex-start" }}>…and {sCount - 3} more, in order.</p>}
          {sEnding.trim() && <div className="bubble bub-a">{sEnding}</div>}
        </div>
        <p className="sub">Afterwards the learner gets the full transcript and a short AI summary — no scores in this format.</p>
      </div>
    </div>
  ) : null;

  /* ── shell ───────────────────────────────────────────────── */
  return (
    <div className="ib">
      <style>{css}</style>
      <header style={{ background: "var(--card)", borderBottom: "1px solid var(--line2)", padding: "10px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", minHeight: 58, position: "relative", zIndex: 3 }}>
        {screen !== "setup" && (
          <>
            <input className="namefld" value={name} onChange={(e) => { setName(e.target.value); touch(); }} aria-label="Interview name" />
            <span className="chip static" style={{ flex: "none" }}>{fmt.name}</span>
            <span className="sub" style={{ flex: "none" }}>{saveState === "Saved" ? "Saved ✓" : "Saving…"}</span>
            <button className="btn-ghost" onClick={() => go("setup")}>Edit setup</button>
            {screen === "workspace" && (
              <button className="btn-ghost" onClick={() => { publishToCommunity("draft"); touch(); }}
                title="Keep it as a draft — visible only to you, editable any time">Save draft</button>
            )}
          </>
        )}
        <div style={{ flex: 1 }} />
        {screen === "workspace" && format === "structured" && order.length > 0 && (
          <button className={"btn" + (allReady && marksOk && modelOk ? " btn-acc" : "")} onClick={() => go("review")}>
            Review & publish · {readyCount}/{order.length}{!marksOk ? ` · ${totalMarks}/100 pts` : ""}
          </button>
        )}
        {screen === "workspace" && format === "simple" && (
          <button className={"btn" + (sBlockers.length === 0 ? " btn-acc" : "")} onClick={() => go("review")}>
            Review & publish · {sCount} q
          </button>
        )}
      </header>

      {screen === "setup" && Setup}
      {screen === "workspace" && (format === "structured" ? Workspace : SimpleWorkspace)}
      {screen === "review" && (format === "structured" ? Review : SimpleReview)}
      {screen === "share" && Share}

      {Preview}
      {SimplePreviewModal}
      {snack && (
        <div className="snack" role="status">
          <span>{snack.msg}</span>
          {snack.undo && <button className="btn-ghost" style={{ color: "#fcd34d" }} onClick={snack.undo}>Undo</button>}
        </div>
      )}
    </div>
  );
}

export default function App({ onExit, onPublish }) {
  return (
    <Boundary>
      <div className="rt-scope" style={{ position: 'relative', minHeight: '100%', background: 'transparent' }}>
        <Builder onExit={onExit} onPublish={onPublish} />
      </div>
    </Boundary>
  );
}
